/**
 * Machine values read into a running batch at the record's read points — `/bp/run/…/capture/` (services/run/capture.py).
 *
 * The server reads the tag, judges the value and decides whether a hand entry is allowed; this client only renders
 * what it is told and sends the person's own value, reason and signature. Every refusal keeps the server's code and
 * message.
 */
import Axios from 'axios';

const bp = '/bp';

const authHeaders = () => {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export class CaptureRefused extends Error {
  constructor(body, status) {
    super(body?.message || 'The request was not accepted.');
    this.name = 'CaptureRefused';
    this.code = body?.code || 'RUN_ERROR';
    this.status = status;
    this.detail = body || {};
  }
}

const call = async (method, url, data, signal) => {
  try {
    const res = await Axios({ method, url, data, signal, withCredentials: true, headers: authHeaders() });
    return res?.data?.data ?? res?.data;
  } catch (e) {
    if (Axios.isCancel?.(e) || e?.name === 'CanceledError') throw e;
    throw new CaptureRefused(e?.response?.data, e?.response?.status);
  }
};

/** The operation's captured values and check rows. */
export const getOperationCapture = (batchNumber, batchConfigId, signal = null) =>
  call('get', `${bp}/run/batches/${encodeURIComponent(batchNumber)}/operations/${batchConfigId}/capture/`, undefined, signal);

/** A captured step's value entered by hand: `{ value, reason, esign: { username, password } }`. Answers `{ step, panel }`. */
export const enterCapturedByHand = (instanceId, body) =>
  call('post', `${bp}/run/instances/${instanceId}/capture/fallback/`, body);

/** Read a captured step's tag again at its read point. Answers `{ step, panel }`. */
export const readCaptured = (instanceId, rank = 1) =>
  call('post', `${bp}/run/instances/${instanceId}/capture/read/`, { rank });

/** Open a check row's earliest due check. Answers `{ step, panel }`. */
export const openDueCheck = (instanceId) => call('post', `${bp}/run/instances/${instanceId}/checks/open/`, {});
